import {
  CircleAlert,
  CircleCheckBig,
  CircleHelp,
  Info,
  OctagonAlert,
  RotateCw,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/envelope';
import type { preflightCampaign } from '../../api/comms';
import type { CampaignAudience, CampaignChannel, CampaignPreflight, CampaignReview } from '../../api/types';
import { useTimeFormat } from '../../lib/tenantTime';
import { useComms } from './context';
import {
  measuredRows,
  type PreflightAction,
  type PreflightGroup,
  type PreflightLevel,
  type PreflightRow,
  reviewRows,
  verdictOf,
  verdictTitle,
} from './preflight';

/**
 * AI reads already paid for in this page's life, by the words they read. The
 * card unmounts whenever the operator steps back to change something, and a
 * review held in its own state would be bought again on the way back.
 * Bounded, oldest out first — it only ever needs the last few versions.
 */
const reviewCache = new Map<string, CampaignReview>();
const REVIEW_CACHE_MAX = 12;
const remember = (signature: string, review: CampaignReview) => {
  reviewCache.delete(signature);
  reviewCache.set(signature, review);
  while (reviewCache.size > REVIEW_CACHE_MAX) {
    const oldest = reviewCache.keys().next().value;
    if (oldest === undefined) break;
    reviewCache.delete(oldest);
  }
};

/** Long enough that typing a subject does not fire a request per keystroke. */
const MEASURE_DEBOUNCE_MS = 450;
const AI_DEBOUNCE_MS = 900;

const GROUPS: { key: PreflightGroup; label: string; step: number }[] = [
  { key: 'sender', label: 'Sender', step: 2 },
  { key: 'audience', label: 'Who gets it', step: 1 },
  { key: 'words', label: 'The words', step: 2 },
  { key: 'sending', label: 'Sending', step: 3 },
];

const LEVEL_ICON: Record<PreflightLevel, typeof CircleCheckBig> = {
  ok: CircleCheckBig,
  warn: CircleAlert,
  risk: OctagonAlert,
  unknown: CircleHelp,
  note: Info,
};

const actionLabel = (action: PreflightAction): string | null => {
  switch (action.kind) {
    case 'open_inbox':
      return 'Open its settings';
    case 'skip_bounced':
      return action.on ? 'Leave them out' : 'Include them';
    case 'frequency_cap':
      return 'Skip them';
    case 'step':
      return 'Change';
    case 'gsm_fix':
      return 'Fix it';
    case 'unsubscribe_on':
      return 'Add it';
    case 'set_window':
      return 'Use 08:00–21:00';
    case 'retry_ai':
      return 'Try again';
    case 'use_suggestion':
      return 'Use this';
    default:
      return null;
  }
};

export type PreflightRequest = Parameters<typeof preflightCampaign>[0];

/**
 * The pre-flight review on Review & send: one verdict over what was MEASURED
 * about the sender and the list (POST /campaigns/preflight), what the builder
 * knows about the words and the sending settings, and an AI read of the words
 * (the campaign review), badged ✦ AI so a second opinion is never mistaken
 * for a fact.
 *
 * The measured half re-runs whenever the inputs change. The AI half runs on
 * its own too — unlike CampaignReviewPanel, which is asked for — because this
 * is the last step before the list gets it, and a review nobody remembers to
 * press is a review that does not happen. It runs once per version of the
 * words: stepping back and forth costs nothing, and only an edit asks again.
 *
 * Nothing here blocks the send. The verdict is information; the button stays
 * the operator's.
 */
export function CampaignPreflightCard({
  request,
  campaignId,
  words,
  localRows,
  heads,
  onAction,
  onStep,
  canApplySuggestion,
}: {
  /** Null while there is no sender or no audience to check yet. */
  request: PreflightRequest | null;
  campaignId: number | null;
  words: {
    channel: CampaignChannel;
    subject: string;
    bodyHtml: string;
    bodyText: string;
    bodySms: string;
    audienceType: CampaignAudience;
    fromName: string;
    /** Nothing written yet — the AI is not asked about an empty draft. */
    empty: boolean;
  };
  /** Rows the builder can answer itself: the sender it has, the words, the sending settings. */
  localRows: PreflightRow[];
  /** The one-line summary beside each group's name. */
  heads: Record<PreflightGroup, string>;
  onAction: (action: PreflightAction) => void;
  onStep: (step: number) => void;
  /** Whether "Use this" can place a suggestion — its words occur exactly once in the copy. */
  canApplySuggestion: (evidence: string) => boolean;
}) {
  const { source, capabilities } = useComms();
  const { listTime } = useTimeFormat();
  const sms = words.channel === 'sms';

  // ---- the measured half ------------------------------------------------
  const requestKey = request ? JSON.stringify({ ...request, campaignId: campaignId ?? undefined }) : null;
  const [measured, setMeasured] = useState<{ key: string; result: CampaignPreflight } | null>(null);
  const [measureError, setMeasureError] = useState<string | null>(null);
  const [measuring, setMeasuring] = useState(false);
  const [measureNonce, setMeasureNonce] = useState(0);
  useEffect(() => {
    if (!requestKey) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setMeasuring(true);
      source.api.preflightCampaign(JSON.parse(requestKey) as PreflightRequest).then(
        (result) => {
          if (cancelled) return;
          setMeasuring(false);
          setMeasureError(null);
          setMeasured({ key: requestKey, result });
        },
        (e: unknown) => {
          if (cancelled) return;
          setMeasuring(false);
          setMeasureError(e instanceof ApiError ? e.message : 'The sender and list checks could not run.');
        },
      );
    }, MEASURE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [source, requestKey, measureNonce]);

  // ---- the AI half -------------------------------------------------------
  const aiLevel = capabilities?.access.features.ai;
  const mayUseAi = aiLevel !== 'off';
  const [aiReady, setAiReady] = useState<boolean | null>(null);
  useEffect(() => {
    if (!mayUseAi) return;
    let cancelled = false;
    source.api.getAiAvailability().then(
      (answer) => {
        if (!cancelled) setAiReady(answer.available);
      },
      () => {
        if (!cancelled) setAiReady(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [source, mayUseAi]);

  const signature = sms ? `sms ${words.bodySms}` : `email ${words.subject}\n${words.bodyHtml}`;
  // Set when an answer lands, so the card re-renders; what it shows is read
  // from the cache, which also holds the answers bought before a step away.
  const [, setReviewed] = useState(0);
  const currentReview = reviewCache.get(signature) ?? null;
  /** The "Check again" press last spent, so one press buys one read. */
  const spentNonce = useRef(0);
  const [aiState, setAiState] = useState<{ signature: string; busy: boolean; error: string | null } | null>(null);
  const [aiNonce, setAiNonce] = useState(0);
  // Read inside the effect rather than listed as a dependency: the request is
  // keyed on the signature, and the rest of `words` riding along must not fire
  // a second paid call when only the audience moved.
  const wordsRef = useRef(words);
  useEffect(() => {
    wordsRef.current = words;
  });
  const wantsAi = mayUseAi && aiReady === true && !words.empty;
  useEffect(() => {
    if (!wantsAi) return;
    // Already read: from this visit, or before a step away. "Check again" (a
    // new nonce) is the only way to pay for the same words twice.
    if (reviewCache.has(signature) && aiNonce === spentNonce.current) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      spentNonce.current = aiNonce;
      const current = wordsRef.current;
      setAiState({ signature, busy: true, error: null });
      source.api
        .reviewCampaign({
          channel: current.channel,
          ...(current.channel === 'sms'
            ? { bodySms: current.bodySms.trim() }
            : {
                subject: current.subject.trim(),
                bodyHtml: current.bodyHtml,
                ...(current.bodyText.trim() ? { bodyText: current.bodyText.trim() } : {}),
              }),
          audienceType: current.audienceType,
          ...(current.fromName.trim() ? { fromName: current.fromName.trim() } : {}),
        })
        .then(
          (result) => {
            remember(signature, result);
            if (cancelled) return;
            setReviewed((count) => count + 1);
            setAiState({ signature, busy: false, error: null });
          },
          (e: unknown) => {
            if (cancelled) return;
            setAiState({
              signature,
              busy: false,
              error: e instanceof ApiError ? e.message : 'The AI read could not run.',
            });
          },
        );
    }, AI_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [source, wantsAi, signature, aiNonce]);

  // ---- the rows ----------------------------------------------------------
  const fresh = measured && measured.key === requestKey ? measured.result : null;
  // The last answer stays up while a newer question is in flight, so the card
  // does not blink on every edit — but not once there is no question at all:
  // with the sender gone, the old sender's rows would describe nothing.
  const shown = requestKey ? (fresh ?? measured?.result ?? null) : null;
  const rows = useMemo(() => {
    const all: PreflightRow[] = [...localRows];
    if (shown) all.push(...measuredRows(shown.checks));
    else if (measureError && request) {
      all.push({
        id: 'm:error',
        group: 'sender',
        level: 'unknown',
        title: 'Couldn’t check the sender and the list',
        detail: measureError,
      });
    }
    if (currentReview) all.push(...reviewRows(currentReview, { sms }));
    else if (aiState?.signature === signature && aiState.error) {
      all.push({
        id: 'w:ai-error',
        group: 'words',
        level: 'unknown',
        title: 'Couldn’t run the AI read of the words',
        detail: aiState.error,
        ai: true,
        action: { kind: 'retry_ai' },
      });
    }
    return all;
  }, [localRows, shown, measureError, request, currentReview, signature, aiState, sms]);

  const verdict = verdictOf(rows);
  const aiBusy = aiState?.signature === signature && aiState.busy;
  const aiPending = wantsAi && !currentReview && !(aiState?.signature === signature && aiState.error);
  const loading = (request && !shown && !measureError) || aiPending;
  const checkedAt = shown?.checkedAt ?? null;

  const act = (action: PreflightAction) => {
    if (action.kind === 'retry_ai') {
      setAiNonce((nonce) => nonce + 1);
      return;
    }
    if (action.kind === 'step') {
      onStep(action.step);
      return;
    }
    onAction(action);
  };

  return (
    <section className="cmg-preflight" data-tour="review-checks" data-card="preflight" aria-label="Pre-flight review">
      <div className="cmg-preflight-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cmg-card-title">Pre-flight review</div>
          <div className="cmg-hint">
            {checkedAt ? `Checked ${listTime(checkedAt)} · ` : ''}runs again when you change something
          </div>
        </div>
        <button
          type="button"
          className="cbtn"
          disabled={measuring || aiBusy}
          title="Run every check again now"
          onClick={() => {
            setMeasureNonce((nonce) => nonce + 1);
            if (wantsAi) setAiNonce((nonce) => nonce + 1);
          }}
        >
          {measuring || aiBusy ? <span className="btn-spin" /> : <RotateCw size={13} />}
          Check again
        </button>
      </div>

      <div className={`cmg-preflight-verdict ${loading ? 'busy' : verdict.level}`}>
        {loading ? <span className="btn-spin" /> : verdict.level === 'ok' ? <CircleCheckBig size={18} /> : verdict.level === 'warn' ? <CircleAlert size={18} /> : <OctagonAlert size={18} />}
        <div>
          <div className="cmg-preflight-verdict-title">{loading ? 'Checking…' : verdictTitle(verdict)}</div>
          <div className="cmg-preflight-verdict-sub">
            {verdict.passed} {verdict.passed === 1 ? 'check' : 'checks'} passed. Sender and list checks are
            measured; rows marked <span className="cmg-ai-badge"><Sparkles size={10} />AI</span> are a second
            opinion on the words.
            {!mayUseAi
              ? ' AI is off for you, so the words are checked by the software only.'
              : aiReady === false
                ? ' AI is not set up for your company, so the words are checked by the software only.'
                : ''}
          </div>
        </div>
      </div>

      {GROUPS.map((group) => {
        const groupRows = rows.filter((row) => row.group === group.key);
        const busy =
          (group.key === 'words' && aiPending) || ((group.key === 'sender' || group.key === 'audience') && request && !shown && !measureError);
        return (
          <div key={group.key} className="cmg-preflight-group" data-group={group.key}>
            <div className="cmg-preflight-group-head">
              <b>{group.label}</b>
              <span className="cmg-preflight-group-sub">{heads[group.key]}</span>
              <div style={{ flex: 1 }} />
              <button type="button" className="cmg-link-btn" onClick={() => onStep(group.step)}>
                {group.key === 'words' ? 'Edit' : 'Change'}
              </button>
            </div>
            {groupRows.map((row) => {
              const Icon = LEVEL_ICON[row.level];
              const label = row.action ? actionLabel(row.action) : null;
              const placeable = row.suggestion ? canApplySuggestion(row.suggestion.evidence) : false;
              return (
                <div key={row.id} className={`cmg-preflight-row ${row.level}`}>
                  <Icon size={15} className="cmg-preflight-icon" />
                  <div className="cmg-preflight-text">
                    <div className="cmg-preflight-title">
                      {row.title}
                      {row.ai && (
                        <span className="cmg-ai-badge">
                          <Sparkles size={10} />
                          AI
                        </span>
                      )}
                    </div>
                    <div className="cmg-preflight-detail">{row.detail}</div>
                    {row.suggestion && (
                      <div className="cmg-preflight-try">
                        <span>
                          Try: <b>{row.suggestion.text}</b>
                        </span>
                        {placeable && (
                          <button
                            type="button"
                            className="cbtn"
                            onClick={() =>
                              act({
                                kind: 'use_suggestion',
                                evidence: row.suggestion?.evidence ?? '',
                                suggestion: row.suggestion?.text ?? '',
                              })
                            }
                          >
                            Use this
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {row.action && label && (
                    <button type="button" className="cbtn cmg-preflight-act" onClick={() => row.action && act(row.action)}>
                      {label}
                    </button>
                  )}
                </div>
              );
            })}
            {busy && (
              <div className="cmg-preflight-row busy">
                <span className="btn-spin" />
                <div className="cmg-preflight-detail">
                  {group.key === 'words' ? 'Reading the words…' : 'Checking…'}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
